import { FileWarning } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { getFileNameFromPath } from '../utils/queryFileUtils';

type Props = {
  open: boolean;
  filePath: string;
  kind: 'changed' | 'deleted';
  hasUnsavedChanges: boolean;
  onKeep: () => void;
  onReload: () => void;
};

export function ExternalFileChangeDialog({
  open,
  filePath,
  kind,
  hasUnsavedChanges,
  onKeep,
  onReload,
}: Props) {
  const fileName = getFileNameFromPath(filePath) || filePath;
  const isDeleted = kind === 'deleted';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onKeep()}>
      <DialogContent className="sm:max-w-md" data-testid="external-file-change-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
              <FileWarning className="h-5 w-5 text-warning" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                {isDeleted ? 'File deleted on disk' : 'File changed on disk'}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm break-all">
                {fileName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm text-foreground leading-relaxed">
          {isDeleted ? (
            <p>This file was removed by another process. Keep your current editor contents and save to re-create it, or close this tab.</p>
          ) : (
            <p>This file was modified by another process while it was open in this tab.</p>
          )}
          {hasUnsavedChanges && !isDeleted && (
            <p className="text-warning">
              You also have unsaved changes — reloading will discard them.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onKeep}
            data-testid="external-file-change-keep"
          >
            Keep my version
          </Button>
          {!isDeleted && (
            <Button
              variant="default"
              onClick={onReload}
              data-testid="external-file-change-reload"
            >
              Reload from file
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
